<template>
  <div
    v-if="show"
    :class="rootClass"
    data-watermark-root
    aria-hidden="true"
  >
    <div class="wm-rotor" :style="rotorStyle">
      <div
        v-for="(tile, i) in tileCount"
        :key="i"
        class="wm-tile"
        :style="tileStyle"
      >
        <div v-for="(line, li) in lines" :key="li" class="wm-line">{{ line }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import WATERMARK_CONFIG from "@/config/watermark.config";
import { buildIdentityLines, isPatientDocComponent } from "@/utils/watermark";

export default {
  name: "WatermarkOverlay",
  data() {
    return {
      now: new Date(),
      timer: null,
      observer: null,
      parentRef: null,
      // Randomized once per mount so a static CSS/script selector can't target it.
      rootClass: "wm-" + Math.random().toString(36).slice(2, 9),
      viewport: { w: 1920, h: 1080 },
    };
  },
  computed: {
    config() {
      return WATERMARK_CONFIG;
    },
    user() {
      return this.$store.getters["moduleUser/getUserInfo"];
    },
    clinic() {
      return this.$store.getters["moduleUser/getClinicInfo"];
    },
    branch() {
      return this.$store.getters["moduleUser/getBranchInfo"];
    },
    show() {
      if (!this.config.enabled || !this.user) return false;
      // Never watermark patient-facing views (e.g. the welcome display). The
      // overlay is global, so we key off the current route name.
      const routeName = this.$route && this.$route.name;
      return !isPatientDocComponent(routeName, this.config.patientDocDenylist);
    },
    lines() {
      if (!this.user) return [];
      return buildIdentityLines({
        user: this.user,
        clinic: this.clinic,
        branch: this.branch,
        now: this.now,
      });
    },
    tileCount() {
      const { width, height } = this.config.tile;
      // The rotor is 200%×200% of the viewport and centered (see CSS), so the
      // rotated layer still blankets the viewport corners. Fill that whole area.
      const cols = Math.ceil((this.viewport.w * 2) / width);
      const rows = Math.ceil((this.viewport.h * 2) / height);
      return cols * rows;
    },
    rotorStyle() {
      return {
        transform: `rotate(${this.config.tile.rotateDeg}deg)`,
        opacity: String(this.config.opacity),
      };
    },
    tileStyle() {
      return {
        width: this.config.tile.width + "px",
        height: this.config.tile.height + "px",
      };
    },
  },
  mounted() {
    this.updateViewport();
    window.addEventListener("resize", this.updateViewport);
    // Refresh the timestamp every minute.
    this.timer = setInterval(() => {
      this.now = new Date();
    }, 60000);
    this.installTamperGuard();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateViewport);
    if (this.timer) clearInterval(this.timer);
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    updateViewport() {
      this.viewport = {
        w: window.innerWidth || 1920,
        h: window.innerHeight || 1080,
      };
    },
    installTamperGuard() {
      // If the root node is removed via devtools, re-attach it to its parent.
      if (typeof MutationObserver === "undefined" || !this.$el || !this.$el.parentNode) return;
      this.parentRef = this.$el.parentNode;
      this.observer = new MutationObserver(() => {
        if (this.show && this.$el && this.parentRef && !this.parentRef.contains(this.$el)) {
          this.parentRef.appendChild(this.$el);
        }
      });
      this.observer.observe(this.parentRef, { childList: true });
    },
  },
};
</script>

<style scoped>
[data-watermark-root] {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 2147483646;
  overflow: hidden;
}
/* Oversized + centered so rotating it still covers the viewport corners.
   Without this, a viewport-sized rotor leaves bare triangles at the corners. */
.wm-rotor {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  transform-origin: center center;
}
.wm-tile {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
  padding: 4px;
}
.wm-line {
  font-size: 12px;
  line-height: 1.4;
  color: #000;
  /* Stay inside the tile: a long name/email/clinic string must wrap (and break
     mid-word if needed) rather than overflow and overlap neighbouring tiles. */
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-family: "Tahoma", sans-serif;
}
@media print {
  [data-watermark-root] {
    display: none !important;
  }
}
</style>
